import {
  apiGet,
  type ApiResponse,
} from "../../services/genesisApi/httpsMethods";
import type {
  UserAllocationCombinationsResponse,
  UserAllocationDeal,
  UserAllocationRole,
  UserAllocationsResponse,
} from "../../types/userAllocations";
import type { Status } from "../../types/deal";

export type DealsActiveFilter = "active" | "inactive" | "all";

export interface UserAllocationsFilters {
  dealsActive: DealsActiveFilter;
  statusIds?: number[];
}

export interface UserAllocationDealsFilters extends UserAllocationsFilters {
  role: UserAllocationRole;
  userId?: number;
  unassigned?: boolean;
  statusId?: number;
  statusUnassigned?: boolean;
}

export interface UserAllocationCombinationDealsFilters
  extends UserAllocationsFilters {
  leadUserId?: number;
  leadUnassigned?: boolean;
  supportUserId?: number;
  supportUnassigned?: boolean;
  statusId?: number;
  statusUnassigned?: boolean;
}

export function activeStatusIdsExcludingClosed(statuses: Status[]): number[] {
  return statuses
    .filter(
      (status) =>
        status.is_active === true &&
        status.status.trim().toLowerCase() !== "closed" &&
        !status.status.trim().toLowerCase().includes("closed"),
    )
    .map((status) => status.id);
}

function appendUserAllocationsFilterParams(
  parts: string[],
  filters: UserAllocationsFilters,
): void {
  if (filters.dealsActive === "active") {
    parts.push("deals_active=true");
  } else if (filters.dealsActive === "inactive") {
    parts.push("deals_active=false");
  }
  for (const id of filters.statusIds ?? []) {
    parts.push(`status_ids=${id}`);
  }
}

export function buildUserAllocationsUrl(
  filters: UserAllocationsFilters,
): string {
  const parts: string[] = [];
  appendUserAllocationsFilterParams(parts, filters);
  const qs = parts.length ? `?${parts.join("&")}` : "";
  return `deals/user-allocations${qs}`;
}

export function buildUserAllocationDealsUrl(
  filters: UserAllocationDealsFilters,
): string {
  const parts: string[] = [];

  if (filters.unassigned) {
    parts.push("unassigned=true");
  } else if (filters.userId != null) {
    parts.push(`user_id=${filters.userId}`);
  }

  parts.push(`role=${filters.role}`);

  if (filters.statusUnassigned) {
    parts.push("status_unassigned=true");
  } else if (filters.statusId != null) {
    parts.push(`status_id=${filters.statusId}`);
  }

  appendUserAllocationsFilterParams(parts, filters);

  return `deals/user-allocations/deals?${parts.join("&")}`;
}

export async function getUserAllocations(
  filters: UserAllocationsFilters,
): Promise<ApiResponse<UserAllocationsResponse>> {
  return apiGet<UserAllocationsResponse>(buildUserAllocationsUrl(filters));
}

export async function getUserAllocationDeals(
  filters: UserAllocationDealsFilters,
): Promise<ApiResponse<UserAllocationDeal[]>> {
  return apiGet<UserAllocationDeal[]>(buildUserAllocationDealsUrl(filters));
}

export function buildUserAllocationCombinationsUrl(
  filters: UserAllocationsFilters,
): string {
  const parts: string[] = [];
  appendUserAllocationsFilterParams(parts, filters);
  const qs = parts.length ? `?${parts.join("&")}` : "";
  return `deals/user-allocations/combinations${qs}`;
}

export function buildUserAllocationCombinationDealsUrl(
  filters: UserAllocationCombinationDealsFilters,
): string {
  const parts: string[] = [];

  if (filters.leadUnassigned) {
    parts.push("lead_unassigned=true");
  } else if (filters.leadUserId != null) {
    parts.push(`lead_user_id=${filters.leadUserId}`);
  }

  if (filters.supportUnassigned) {
    parts.push("support_unassigned=true");
  } else if (filters.supportUserId != null) {
    parts.push(`support_user_id=${filters.supportUserId}`);
  }

  if (filters.statusUnassigned) {
    parts.push("status_unassigned=true");
  } else if (filters.statusId != null) {
    parts.push(`status_id=${filters.statusId}`);
  }

  appendUserAllocationsFilterParams(parts, filters);

  return `deals/user-allocations/combinations/deals?${parts.join("&")}`;
}

export async function getUserAllocationCombinations(
  filters: UserAllocationsFilters,
): Promise<ApiResponse<UserAllocationCombinationsResponse>> {
  return apiGet<UserAllocationCombinationsResponse>(
    buildUserAllocationCombinationsUrl(filters),
  );
}

export async function getUserAllocationCombinationDeals(
  filters: UserAllocationCombinationDealsFilters,
): Promise<ApiResponse<UserAllocationDeal[]>> {
  return apiGet<UserAllocationDeal[]>(
    buildUserAllocationCombinationDealsUrl(filters),
  );
}
